/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Grid, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_ISSUE_DELETE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { IssueSelector } from '../../common/ui/issue-selector';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const styles = cssMap({
	errorMessage: {
		paddingInlineStart: token('space.025'),
	},

	grid: {
		width: '100%',
		gridTemplateAreas: '"title close"',
	},

	titleContainer: {
		gridArea: 'title',
	},
});

type JiraIssueDeleteAction = ActionWith<
	ActionRendererKeyToPayload[typeof ActionKeys.JiraIssueDelete]
>;

const confirmIssueDelete = ({
	actions,
	firstAction,
	getStatus,
	selectedIssuesData,
	onCustomDataConfirm,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: {
	actions: JiraIssueDeleteAction[];
	firstAction: JiraIssueDeleteAction;
	getStatus: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>['getStatus'];
	selectedIssuesData: JiraIssueDeleteAction['data']['issues'];
	onCustomDataConfirm: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>['onCustomDataConfirm'];
	sendMessageConfirmedTools: ActionRendererFor<
		typeof ActionKeys.JiraIssueDelete
	>['sendMessageConfirmedTools'];
	recipientAgentId: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>['recipientAgentId'];
	conversationId: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>['conversationId'];
	messageId: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>['messageId'];
}) => {
	if (fg('rovo_tools_resumption_migration_phase2')) {
		const plannedActions = actions.filter((action) => getStatus(action) === 'idle');

		sendMessageConfirmedTools({
			agentId: recipientAgentId,
			conversationId,
			messageSources: {
				interactionSource: 'jiraIssueDeleteRenderer',
			},
			confirmed_tools: {
				message_id: messageId,
				tools: plannedActions.map((action) =>
					action.invocationId === firstAction.invocationId
						? buildToolFromAction(action, 'confirmed', {
								...action.data,
								issues: selectedIssuesData,
							})
						: buildToolFromAction(action, 'cancelled'),
				),
			},
		});

		return;
	}

	onCustomDataConfirm(firstAction, {
		issues: selectedIssuesData,
	});
};

export const filterJiraIssueDeleteActions = (actions: MessageAction[]): JiraIssueDeleteAction[] => {
	return actions.filter(assertIsJiraIssueDeleteAction) as JiraIssueDeleteAction[];
};

const assertIsJiraIssueDeleteAction = (action: MessageAction): MessageAction => {
	if (action.key !== JIRA_ISSUE_DELETE_KEY) {
		throw new Error('Invalid action key for JiraIssueDeleteAction');
	}
	return action;
};

export const JiraIssueDeleteAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>,
): JSX.Element => {
	return <JiraIssueDeleteActionNewLayout {...props} />;
};

export const JiraIssueDeleteActionNewLayout = ({
	actions,
	getStatus,
	getReadonly,
	recipientAgentNamedId,
	recipientAgentId,
	conversationId,
	messageId,
	onCustomDataConfirm,
	onCancel,
	sendMessageConfirmedTools,
}: ActionRendererFor<typeof ActionKeys.JiraIssueDelete>): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
	const [shouldShowErrorMessage, setShouldShowErrorMessage] = useState(false);

	const { sendAnalyticsEvent } = useAnalytics();

	const firstAction = actions[0];
	const {
		data: { issues },
	} = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);

	const openDeleteModal = useCallback(() => {
		sendAnalyticsEvent({
			action: 'appeared',
			actionSubject: 'dialog',
			actionSubjectId: 'confirmDeleteModal',
			attributes: {
				recipientAgentNamedId,
				actionKey: JIRA_ISSUE_DELETE_KEY,
			},
		});
		setIsModalOpen(true);
	}, [sendAnalyticsEvent, recipientAgentNamedId]);

	const closeDeleteModal = useCallback(() => {
		onCancel(firstAction);
		setIsModalOpen(false);
	}, [firstAction, onCancel]);

	const handleSelectIssue = useCallback(
		(issueKeys: string[]) => {
			setSelectedIssues(issueKeys);
			if (shouldShowErrorMessage) {
				setShouldShowErrorMessage(issueKeys.length === 0);
			}
		},
		[shouldShowErrorMessage],
	);

	const handleDeleteButton = useCallback(() => {
		const canOpenModal = selectedIssues.length > 0;
		if (canOpenModal) {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'deleteActionButton',
				attributes: {
					recipientAgentNamedId,
					actionKey: JIRA_ISSUE_DELETE_KEY,
					selectedIssueCount: selectedIssues.length,
					totalIssueCount: issues.length,
				},
			});
			openDeleteModal();
		}
		setShouldShowErrorMessage(!canOpenModal);
	}, [
		issues.length,
		openDeleteModal,
		recipientAgentNamedId,
		selectedIssues.length,
		sendAnalyticsEvent,
	]);

	const deleteConfirm = () => {
		const selectedIssuesData = issues.filter(({ issueKey }) => selectedIssues.includes(issueKey));
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'deleteActionConfirmButton',
			attributes: {
				recipientAgentNamedId,
				actionKey: JIRA_ISSUE_DELETE_KEY,
			},
		});
		confirmIssueDelete({
			actions,
			firstAction,
			getStatus,
			selectedIssuesData,
			onCustomDataConfirm,
			sendMessageConfirmedTools,
			recipientAgentId,
			conversationId,
			messageId,
		});
		setIsModalOpen(false);
	};

	if (issues.length <= 0) {
		return null;
	}

	return (
		<Stack space="space.250">
			<ActionCardWrapper
				product="jira"
				title={formatMessage(messages.jiraIssueDeleteActionCardTitle)}
			>
				<Stack space="space.150">
					<IssueSelector
						initialIssues={issues}
						title={formatMessage(messages.jiraIssueDeleteListTitleText)}
						isInitiallyExpanded
						onIssuesSelected={handleSelectIssue}
						readonly={readonly}
					/>

					<ActionCardFooter>
						<Button
							appearance="primary"
							isDisabled={readonly || status !== 'idle'}
							onClick={handleDeleteButton}
						>
							{formatMessage(messages.jiraIssueDeleteButtonLabelText)}
						</Button>
					</ActionCardFooter>
				</Stack>
			</ActionCardWrapper>
			<div role="region" aria-live="polite">
				{shouldShowErrorMessage && (
					<Inline alignBlock="center" space="space.075" xcss={styles.errorMessage}>
						<ErrorIcon label="" color={token('color.icon.danger')} size="small" />
						<Text color="color.text.danger" size="small">
							{formatMessage(messages.jiraIssueDeleteButtonErrorMessageText)}
						</Text>
					</Inline>
				)}
			</div>
			<ModalTransition>
				{isModalOpen && (
					<Modal onClose={closeDeleteModal}>
						<ModalHeader hasCloseButton>
							<Grid gap="space.200" xcss={styles.grid}>
								<Flex xcss={styles.titleContainer} justifyContent="start">
									<ModalTitle appearance="danger">
										{formatMessage(messages.jiraIssueDeleteModalDialogTitleText)}
									</ModalTitle>
								</Flex>
							</Grid>
						</ModalHeader>
						<ModalBody>
							<Text>
								{formatMessage(messages.jiraIssueDeleteModalDialogBodyText, {
									s: selectedIssues && selectedIssues.length > 1 ? 's' : '',
								})}
							</Text>
						</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={closeDeleteModal}>
								{formatMessage(messages.jiraIssueDeleteModalDialogCancelButton)}
							</Button>
							<Button appearance="danger" onClick={deleteConfirm}>
								{formatMessage(messages.jiraIssueDeleteModalDialogConfirmButton)}
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</Stack>
	);
};
